/**
 * IndexedDB 实现 —— 13 个 store + 复合索引 + 消息分页。
 *
 * 表结构（version 1，ARCH §4.5）：
 * | store | keyPath | 索引 |
 * | keystore | id(固定 'default') | — |
 * | identity | id | — |
 * | contacts | peerId | did, updatedAt |
 * | messages | id | [peerId+ts], msgId, ts |
 * | pending | msgId | to |
 * | posts | id | ts, authorDid |
 * | profiles | did | peerId |
 * | blobs | cid | ts |
 * | txs | txHash | [address+ts], ts |
 * | channels | channelId | payer |
 * | reports | id | ts |
 * | traffic | dayKey | — |
 * | settings | key | — |
 * | purchases | key | — |
 * | contentKeys | key | — |
 *
 * version 2 新增 `purchases` / `contentKeys`（创作者付费内容）。升级回调逐表
 * `contains()` 守卫，老库（v1）升级时只补建缺失的表，不动既有数据。
 */

import { openDB, deleteDB, type DBSchema, type IDBPDatabase, type StoreNames } from 'idb';

import type {
  Contact,
  CachedBlob,
  ContentKeyRecord,
  DayTraffic,
  IdentityMeta,
  Message,
  MsgStatus,
  PendingItem,
  Post,
  Profile,
  PurchaseRecord,
  Report,
} from '@shared/types';
import type { Channel, TxRecord } from '@shared/chain';
import type { EncryptedKeystore } from '@/crypto/keystore';
import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';
import { dbNameFor, type IStorage, type KVRepo, type MessageRepo, type Repo, type StorageUsage } from './IStorage';

const logger = createLogger('storage');

const DB_VERSION = 2;
const MESSAGE_PAGE_SIZE = 30;

interface MeshLinkDB extends DBSchema {
  keystore: { key: string; value: EncryptedKeystore };
  identity: { key: string; value: IdentityMeta & { id: string } };
  contacts: { key: string; value: Contact; indexes: { did: string; updatedAt: number } };
  messages: {
    key: string;
    value: Message;
    indexes: { 'peerId+ts': [string, number]; msgId: string; ts: number };
  };
  pending: { key: string; value: PendingItem; indexes: { to: string } };
  posts: { key: string; value: Post; indexes: { ts: number; authorDid: string } };
  profiles: { key: string; value: Profile; indexes: { peerId: string } };
  blobs: { key: string; value: CachedBlob; indexes: { ts: number } };
  txs: { key: string; value: TxRecord; indexes: { 'address+ts': [string, number]; ts: number } };
  channels: { key: string; value: Channel; indexes: { payer: string } };
  reports: { key: string; value: Report; indexes: { ts: number } };
  traffic: { key: string; value: DayTraffic };
  settings: { key: string; value: { key: string; value: unknown } };
  purchases: { key: string; value: { key: string; value: PurchaseRecord } };
  contentKeys: { key: string; value: { key: string; value: ContentKeyRecord } };
}

const STORE_NAMES = [
  'keystore',
  'identity',
  'contacts',
  'messages',
  'pending',
  'posts',
  'profiles',
  'blobs',
  'txs',
  'channels',
  'reports',
  'traffic',
  'settings',
  'purchases',
  'contentKeys',
] as const satisfies ReadonlyArray<keyof MeshLinkDB>;

type StoreName = StoreNames<MeshLinkDB>;

function idbKeyRange(): typeof IDBKeyRange {
  const kr = (globalThis as { IDBKeyRange?: typeof IDBKeyRange }).IDBKeyRange;
  if (!kr) throw new MeshLinkError('E_STORAGE_UNAVAILABLE', '当前环境不支持 IndexedDB');
  return kr;
}

/** 兜底判断是否为「浏览器存储被禁用」类错误。 */
function isStorageUnavailable(err: unknown): boolean {
  const e = err as { name?: string } | null;
  return !!e && (e.name === 'InvalidStateError' || e.name === 'SecurityError');
}

export class IndexedDbStorage implements IStorage {
  private db: IDBPDatabase<MeshLinkDB> | null = null;
  private name = '';

  get ready(): boolean {
    return this.db !== null;
  }

  get dbName(): string {
    return this.name;
  }

  async open(did: string): Promise<void> {
    this.name = dbNameFor(did);
    try {
      this.db = await openDB<MeshLinkDB>(this.name, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('keystore')) db.createObjectStore('keystore', { keyPath: 'id' });
          if (!db.objectStoreNames.contains('identity')) db.createObjectStore('identity', { keyPath: 'id' });

          if (!db.objectStoreNames.contains('contacts')) {
            const s = db.createObjectStore('contacts', { keyPath: 'peerId' });
            s.createIndex('did', 'did');
            s.createIndex('updatedAt', 'updatedAt');
          }
          if (!db.objectStoreNames.contains('messages')) {
            const s = db.createObjectStore('messages', { keyPath: 'id' });
            s.createIndex('peerId+ts', ['peerId', 'ts']);
            s.createIndex('msgId', 'msgId');
            s.createIndex('ts', 'ts');
          }
          if (!db.objectStoreNames.contains('pending')) {
            const s = db.createObjectStore('pending', { keyPath: 'msgId' });
            s.createIndex('to', 'to');
          }
          if (!db.objectStoreNames.contains('posts')) {
            const s = db.createObjectStore('posts', { keyPath: 'id' });
            s.createIndex('ts', 'ts');
            s.createIndex('authorDid', 'authorDid');
          }
          if (!db.objectStoreNames.contains('profiles')) {
            const s = db.createObjectStore('profiles', { keyPath: 'did' });
            s.createIndex('peerId', 'peerId');
          }
          if (!db.objectStoreNames.contains('blobs')) {
            const s = db.createObjectStore('blobs', { keyPath: 'cid' });
            s.createIndex('ts', 'ts');
          }
          if (!db.objectStoreNames.contains('txs')) {
            const s = db.createObjectStore('txs', { keyPath: 'txHash' });
            s.createIndex('address+ts', ['from', 'timestamp']);
            s.createIndex('ts', 'timestamp');
          }
          if (!db.objectStoreNames.contains('channels')) {
            const s = db.createObjectStore('channels', { keyPath: 'channelId' });
            s.createIndex('payer', 'payer');
          }
          if (!db.objectStoreNames.contains('reports')) {
            const s = db.createObjectStore('reports', { keyPath: 'id' });
            s.createIndex('ts', 'ts');
          }
          if (!db.objectStoreNames.contains('traffic')) db.createObjectStore('traffic', { keyPath: 'dayKey' });
          if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings', { keyPath: 'key' });
          if (!db.objectStoreNames.contains('purchases')) db.createObjectStore('purchases', { keyPath: 'key' });
          if (!db.objectStoreNames.contains('contentKeys')) db.createObjectStore('contentKeys', { keyPath: 'key' });
        },
      });
      logger.info('IndexedDB 已打开', { db: this.name });
    } catch (err) {
      if (isStorageUnavailable(err)) throw new MeshLinkError('E_STORAGE_UNAVAILABLE', undefined, err);
      throw new MeshLinkError('E_STORAGE_UNAVAILABLE', undefined, err);
    }
  }

  async close(): Promise<void> {
    this.db?.close();
    this.db = null;
  }

  private mustDb(): IDBPDatabase<MeshLinkDB> {
    if (!this.db) throw new MeshLinkError('E_STORAGE_UNAVAILABLE', '存储未打开');
    return this.db;
  }

  // ─────────────── keystore / identity ───────────────

  async getKeystore(): Promise<EncryptedKeystore | null> {
    const value = await this.mustDb().get('keystore', 'default');
    return value ?? null;
  }

  async putKeystore(keystore: EncryptedKeystore): Promise<void> {
    await this.mustDb().put('keystore', keystore);
  }

  async getIdentityMeta(): Promise<IdentityMeta | null> {
    const value = await this.mustDb().get('identity', 'default');
    if (!value) return null;
    const { id: _id, ...meta } = value;
    return meta as IdentityMeta;
  }

  async putIdentityMeta(meta: IdentityMeta): Promise<void> {
    await this.mustDb().put('identity', { ...meta, id: 'default' });
  }

  // ─────────────── 通用 Repo 工厂 ───────────────

  private makeRepo<K extends StoreName>(store: K): Repo<MeshLinkDB[K]['value']> {
    return {
      get: async (key) => (await this.mustDb().get(store, key as never)) ?? undefined,
      put: async (value) => {
        await this.mustDb().put(store, value as never);
      },
      putMany: async (values) => {
        const tx = this.mustDb().transaction(store, 'readwrite');
        await Promise.all(values.map((v) => tx.store.put(v as never)));
        await tx.done;
      },
      delete: async (key) => {
        await this.mustDb().delete(store, key as never);
      },
      all: async () => (await this.mustDb().getAll(store)) as MeshLinkDB[K]['value'][],
      count: async () => this.mustDb().count(store),
      clear: async () => {
        await this.mustDb().clear(store);
      },
    };
  }

  // ─────────────── 领域仓储 ───────────────

  readonly contacts: Repo<Contact> = {
    get: async (key) => (await this.mustDb().get('contacts', key as string)) ?? undefined,
    put: async (value) => {
      await this.mustDb().put('contacts', value);
    },
    putMany: async (values) => {
      const tx = this.mustDb().transaction('contacts', 'readwrite');
      await Promise.all(values.map((v) => tx.store.put(v)));
      await tx.done;
    },
    delete: async (key) => {
      await this.mustDb().delete('contacts', key as string);
    },
    all: async () => this.mustDb().getAll('contacts'),
    count: async () => this.mustDb().count('contacts'),
    clear: async () => {
      await this.mustDb().clear('contacts');
    },
  };

  readonly messages: MessageRepo = {
    get: async (key) => (await this.mustDb().get('messages', key as string)) ?? undefined,
    put: async (value) => {
      await this.mustDb().put('messages', value);
    },
    putMany: async (values) => {
      const tx = this.mustDb().transaction('messages', 'readwrite');
      await Promise.all(values.map((v) => tx.store.put(v)));
      await tx.done;
    },
    delete: async (key) => {
      await this.mustDb().delete('messages', key as string);
    },
    all: async () => this.mustDb().getAll('messages'),
    count: async () => this.mustDb().count('messages'),
    clear: async () => {
      await this.mustDb().clear('messages');
    },

    /** 按会话倒序分页：复合索引 `[peerId+ts]` 上从 beforeTs 往回取 limit 条。 */
    page: async (peerId: string, beforeTs?: number, limit = MESSAGE_PAGE_SIZE): Promise<Message[]> => {
      const db = this.mustDb();
      const upper = beforeTs ?? Number.MAX_SAFE_INTEGER;
      const range = idbKeyRange().bound([peerId, -Infinity], [peerId, upper]);
      const tx = db.transaction('messages', 'readonly');
      const index = tx.store.index('peerId+ts');
      const out: Message[] = [];
      let cursor = await index.openCursor(range, 'prev');
      while (cursor && out.length < limit) {
        out.push(cursor.value);
        cursor = await cursor.continue();
      }
      await tx.done;
      return out.reverse(); // 翻页取出是倒序，翻回正序便于渲染
    },

    byMsgId: async (msgId: string): Promise<Message | null> => {
      const value = await this.mustDb().getFromIndex('messages', 'msgId', msgId);
      return value ?? null;
    },

    updateStatus: async (msgId: string, status: MsgStatus): Promise<void> => {
      const db = this.mustDb();
      const tx = db.transaction('messages', 'readwrite');
      const existing = await tx.store.index('msgId').get(msgId);
      if (existing) {
        await tx.store.put({ ...existing, status });
      }
      await tx.done;
    },

    countUnread: async (peerId: string): Promise<number> => {
      const all = await this.mustDb().getAll('messages');
      return all.filter((m) => m.peerId === peerId && m.dir === 'in' && m.status !== 'read').length;
    },

    deleteByPeer: async (peerId: string): Promise<void> => {
      const tx = this.mustDb().transaction('messages', 'readwrite');
      let cursor = await tx.store.index('peerId+ts').openCursor(idbKeyRange().bound([peerId, -Infinity], [peerId, Infinity]));
      while (cursor) {
        await cursor.delete();
        cursor = await cursor.continue();
      }
      await tx.done;
    },
  };

  readonly pending: Repo<PendingItem> = this.makeRepo('pending');
  readonly posts: Repo<Post> = this.makeRepo('posts');
  readonly profiles: Repo<Profile> = this.makeRepo('profiles');
  readonly blobs: Repo<CachedBlob> = this.makeRepo('blobs');
  readonly txs: Repo<TxRecord> = this.makeRepo('txs');
  readonly channels: Repo<Channel> = this.makeRepo('channels');
  readonly reports: Repo<Report> = this.makeRepo('reports');
  readonly traffic: Repo<DayTraffic> = this.makeRepo('traffic');

  readonly settings: KVRepo<unknown> = {
    get: async (key) => {
      const row = await this.mustDb().get('settings', key);
      return row?.value;
    },
    set: async (key, value) => {
      await this.mustDb().put('settings', { key, value });
    },
    delete: async (key) => {
      await this.mustDb().delete('settings', key);
    },
    keys: async () => {
      const rows = await this.mustDb().getAll('settings');
      return rows.map((r) => r.key);
    },
    clear: async () => {
      await this.mustDb().clear('settings');
    },
  };

  /** 我的已购内容记录（创作者交付密钥缓存）。 */
  readonly purchases: KVRepo<PurchaseRecord> = {
    get: async (key) => {
      const row = await this.mustDb().get('purchases', key);
      return row?.value;
    },
    set: async (key, value) => {
      await this.mustDb().put('purchases', { key, value });
    },
    delete: async (key) => {
      await this.mustDb().delete('purchases', key);
    },
    keys: async () => {
      const rows = await this.mustDb().getAll('purchases');
      return rows.map((r) => r.key);
    },
    clear: async () => {
      await this.mustDb().clear('purchases');
    },
  };

  /** 我作为创作者的内容密钥（向已付款买家交付）。 */
  readonly contentKeys: KVRepo<ContentKeyRecord> = {
    get: async (key) => {
      const row = await this.mustDb().get('contentKeys', key);
      return row?.value;
    },
    set: async (key, value) => {
      await this.mustDb().put('contentKeys', { key, value });
    },
    delete: async (key) => {
      await this.mustDb().delete('contentKeys', key);
    },
    keys: async () => {
      const rows = await this.mustDb().getAll('contentKeys');
      return rows.map((r) => r.key);
    },
    clear: async () => {
      await this.mustDb().clear('contentKeys');
    },
  };

  // ─────────────── 运维 ───────────────

  async estimateUsage(): Promise<StorageUsage> {
    try {
      const est = await navigator.storage?.estimate?.();
      return { usageBytes: est?.usage ?? 0, quotaBytes: est?.quota ?? 0 };
    } catch {
      return { usageBytes: 0, quotaBytes: 0 };
    }
  }

  /** 注销：清空所有 store（含 keystore）。 */
  async clearAll(): Promise<void> {
    const db = this.mustDb();
    const tx = db.transaction(STORE_NAMES as unknown as StoreName[], 'readwrite');
    await Promise.all(
      STORE_NAMES.map((name) => (tx.objectStore(name as StoreName) as unknown as { clear(): Promise<void> }).clear()),
    );
    await tx.done;
  }

  /** 删除整个库（注销时用，比 clearAll 更彻底）。 */
  async dropDatabase(): Promise<void> {
    const name = this.name;
    await this.close();
    if (name) await deleteDB(name);
  }
}

export { STORE_NAMES, DB_VERSION };
